import { api } from './api';
import { SupportTicket, SupportTicketListResponse, ChatMessage, Id, TicketEvent, TicketNote, CannedReply } from '../types/Support';

// GET all support tickets (paginated & filtered)
export const listSupportTickets = (
    page: number = 1,
    limit: number = 20,
    filters?: { status?: string; issueType?: string; rideId?: number; priority?: string; cityId?: string; assigneeId?: number; unassigned?: boolean; from?: string; to?: string; category?: string; queue?: string }
) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.issueType) params.append('issueType', filters.issueType);
    if (filters?.rideId) params.append('rideId', filters.rideId.toString());
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.cityId) params.append('cityId', filters.cityId);
    if (typeof filters?.assigneeId === 'number') params.append('assigneeId', filters.assigneeId.toString());
    if (filters?.unassigned) params.append('unassigned', 'true');
    if (filters?.from) params.append('from', filters.from);
    if (filters?.to) params.append('to', filters.to);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.queue) params.append('queue', filters.queue);

    return api.get<SupportTicketListResponse>(`/admin/support-tickets?${params.toString()}`);
};

// GET single ticket details
export const getSupportTicket = (id: Id) => {
    return api.get<SupportTicket>(`/admin/support-ticket/${id}`);
};

// UPDATE ticket (status/priority/assignee/tags/…). Backend value-validates → 400 on bad enum.
export const updateSupportTicket = (id: Id, data: Partial<SupportTicket>) => {
    return api.patch<SupportTicket>(`/admin/support-ticket/${id}`, data);
};

// GET chat history for a ticket
export const getTicketMessages = (id: Id) => {
    return api.get<ChatMessage[]>(`/admin/support-ticket/${id}/messages`);
};

// POST a new message (reply)
export const sendTicketMessage = (id: Id, content: string) => {
    return api.post<ChatMessage>(`/admin/support-ticket/${id}/message`, { content });
};

// Claim a ticket (optimistic lock — first agent wins; 409 if already claimed).
export const claimSupportTicket = (id: Id) => {
    return api.post<{ message: string; claimed: boolean }>(`/admin/support-ticket/${id}/claim`, {});
};

// (Re)assign a ticket to an agent (or null to unassign).
export const assignSupportTicket = (id: Id, assigneeId: number | null) => {
    return api.post<{ message: string; assigneeId: number | null }>(`/admin/support-ticket/${id}/assign`, { assigneeId });
};

// Assignable-staff directory (ACTIVE staff, slim payload) for the assign picker + assignee names.
// Support v2 taxonomy: category → sub-category (level P0–P3, queue, side) for pickers and filters.
export interface SupportSubCategory { id: string; label: string; level: 'P0' | 'P1' | 'P2' | 'P3'; queue: string; side: string; legacy: string; misconduct?: boolean }
export interface SupportCategory { id: string; label: string; subs: SupportSubCategory[] }
export const getSupportTaxonomy = () => api.get<{ categories: SupportCategory[] }>(`/admin/support-taxonomy`);

// Phase 2 desk: my status / capacity / skills and the agents board
export interface AgentBoardRow { id: number; name: string; role: string; status: string; capacity: number; queues: string[]; languages: string[]; load: number }
export const getMyDesk = () => api.get<{ staffId: number; status: string; capacity: number; queues: string[]; languages: string[] }>(`/admin/support/me/profile`);
export const updateMyDesk = (body: { status?: string; capacity?: number; queues?: string[]; languages?: string[] }) =>
    api.put<{ profile: { status: string }; handOver: { id: string; publicNumber: string | null }[] }>(`/admin/support/me/profile`, body);
export const getAgentBoard = () => api.get<{ agents: AgentBoardRow[] }>(`/admin/support/agents/board`);
// Phase 2 §5.9: automation rules (read-only list).
export interface AutomationRow { key: string; label: string; switchable: boolean; enabled: boolean }
export const listAutomations = () => api.get<{ automations: AutomationRow[] }>(`/admin/support/automations`);

// Phase 2 §5.10 quality: QA review of a finished ticket, review sample, per-agent stats.
export interface QaReview { id: string; ticketId: string; agentId: number | null; reviewerId: number; greeting: boolean; correctAnswer: boolean; policy: boolean; tone: boolean; closure: boolean; score: number; note: string | null }
export interface QaSampleRow { id: string; publicNumber: string | null; agentId: number; subCategory: string | null; csatScore: number | null; resolvedAt: string | null }
export interface AgentStatsRow { agentId: number; name: string; handled: number; resolved: number; avgFirstReplyMin: number | null; avgResolveMin: number | null; csatAvg: number | null; csatCount: number; lowCsat: number; reopenRate: number | null; qaAvg: number | null; qaCount: number }
export const getQaReview = (id: Id) => api.get<{ review: QaReview | null }>(`/admin/support-ticket/${id}/qa`);
export const submitQaReview = (id: Id, body: { greeting: boolean; correctAnswer: boolean; policy: boolean; tone: boolean; closure: boolean; note?: string }) =>
    api.post<{ id: string; score: number }>(`/admin/support-ticket/${id}/qa`, body);
export const getQaSample = (days: number) => api.get<{ tickets: QaSampleRow[] }>(`/admin/support/qa/sample`, { params: { days } });
export const getAgentStats = (days: number) => api.get<{ agents: AgentStatsRow[] }>(`/admin/support/qa/stats`, { params: { days } });

// Phase 2 §12: support analytics (live desk + period report).
type Counts = Record<string, number>;
export interface SupportAnalyticsResponse {
    live: { openByQueue: Counts; slaAtRisk: number; activeSos: number; onDutyAgents: number };
    period: {
        days: number; capped: boolean;
        volume: { total: number; byCategory: Counts; byQueue: Counts; byCity: Counts; byHourIst: number[] };
        firstReply: { medianMin: number | null; p90Min: number | null; slaMetPct: number | null };
        resolution: { medianMin: number | null; p90Min: number | null; slaMetPct: number | null };
        reopenRatePct: number | null;
        csat: { avg: number | null; count: number; low: number; reasons: Counts };
        repeatContacts: number;
        backlog: { open: number; byAge: Counts };
        bot: { byOutcome: Counts; solved: number; handedOff: number; deflectionPct: number | null };
        sos: { count: number; acknowledged: number; ackMedianSec: number | null; byOutcome: Counts };
        topDrivers: { driverId: string; complaints: number }[];
    };
}
export const getSupportAnalytics = (days: number) => api.get<SupportAnalyticsResponse>(`/admin/support/analytics`, { params: { days } });

// Phase 2 §5.8: approvals
export interface ApprovalRow { id: string; ticketId: string; kind: string; amount: number | null; note: string; status: string; requestedBy: number; decidedBy: number | null; paidAt?: string | null; paidNote?: string | null }
export const requestApproval = (ticketId: Id, body: { kind: string; note: string; amount?: number }) => api.post(`/admin/support-ticket/${ticketId}/approvals`, body);
export const listApprovals = (q: { status?: string; ticketId?: Id }) => {
    const params = new URLSearchParams();
    if (q.status) params.append('status', q.status);
    if (q.ticketId != null) params.append('ticketId', String(q.ticketId));
    return api.get<{ approvals: ApprovalRow[]; cap?: number; approvedTotal?: number }>(`/admin/support/approvals?${params.toString()}`);
};
export const decideApproval = (id: string, decision: 'approve' | 'reject', note?: string) => api.post(`/admin/support/approvals/${id}/decide`, { decision, note });

// Round 2 F11: approved compensation / fee waivers still to pay, and marking them paid (no money moves here).
export interface PayableRow {
    id: string; ticketId: string; publicNumber: string | null; rideId: number | null;
    customerName: string | null; customerType: string | null;
    kind: string; amount: number | null; note: string;
    requestedByName: string | null; decidedByName: string | null; decidedAt: string | null;
    paidAt: string | null; paidByName: string | null; paidNote: string | null;
}
export const listPayables = (tab: 'to_pay' | 'paid', page = 0) =>
    api.get<{ items: PayableRow[]; page: number; hasMore: boolean }>(`/admin/support/approvals/payables`, { params: { tab, page } });
export const markApprovalPaid = (id: string, note: string) => api.post(`/admin/support/approvals/${id}/paid`, { note });

// R2b-4: Support settings (the support App Config keys under one roof; validated on the server).
export interface SupportSetting {
    key: string; group: string; label: string; help: string; editableBy: 'lead' | 'admin'; applies: string; value: any; isDefault: boolean;
}
export const getSupportSettings = () => api.get<{ settings: SupportSetting[] }>(`/admin/support/settings`);
// `before` = the value this page loaded: the server answers 409 if someone saved in between (adversarial R1).
export const saveSupportSetting = (key: string, value: unknown, before?: unknown) => api.put<{ key: string; value: any }>(`/admin/support/settings/${key}`, { value, before });

// Phase 2 §5.6: the customer card
export interface Customer360 {
    customer: { name: string | null; kind: string; city: string | null; language: string | null; accountAgeDays: number | null };
    ride: { id: number; status: string; totalFare: number | null; pickupLocation?: string; dropoffLocation?: string; paymentMethod?: string | null; paymentStatus?: string | null; fraud: { fraudPct: number; status: string } | null } | null;
    recentRides: { id: number; status: string; totalFare: number | null; requestedAt: string }[];
    tickets: { id: string; publicNumber: string | null; status: string; csatScore: number | null }[];
    csat: { count: number; average: number | null };
    driver: { status: string; documentsVerified: boolean; rating: number } | null;
}
export const getCustomer360 = (id: Id) => api.get<Customer360>(`/admin/support-ticket/${id}/customer360`);

// Phase 2 §5.7: canned replies with this ticket's fill-ins; one-tap macros
export const getTicketCanned = (id: Id) => api.get<{ replies: { label: string; text: string }[] }>(`/admin/support-ticket/${id}/canned`);
export const listMacros = () => api.get<{ macros: { id: string; label: string; status: string | null; resolutionCode: string | null }[] }>(`/admin/support/macros`);
export const applyMacro = (id: Id, macroId: string) => api.post<{ applied: string; text: string }>(`/admin/support-ticket/${id}/macro`, { macroId });

// Phase 2: escalate one tier (L1 → L2 → admin) with a reason
export const escalateSupportTicket = (id: Id, body: { reason: string; note?: string }) =>
    api.post<{ escalationTier: string; escalated: boolean }>(`/admin/support-ticket/${id}/escalate`, body);

// Phase 2: transfer to an agent and / or queue with a note
export const transferSupportTicket = (id: Id, body: { toAgentId: number | null; toQueue: string | null; note: string }) =>
    api.post<{ assigneeId: number | null; queue: string | null }>(`/admin/support-ticket/${id}/transfer`, body);

// §5.11: the requester's full number for a call-back (the server logs who asked)
export const revealRequesterPhone = (id: Id) => api.post<{ phone: string | null }>(`/admin/support-ticket/${id}/reveal-phone`, {});

// MVAG: a person's suspend decision (needs drivers:write; recording only — never an automatic suspension)
export const recordSuspendDecision = (id: Id, decision: 'suspend' | 'no_suspend', reason: string) =>
    api.post(`/admin/support-ticket/${id}/suspend-decision`, { decision, reason });

export const listSupportAgents = () => {
    return api.get<{ agents: { id: number; name: string; role: string }[] }>(`/admin/support-agents`);
};

// Ticket audit trail — created / assigned / sla_breach / csat / reopened / … for the Timeline tab.
export const getTicketEvents = (id: Id) => {
    return api.get<{ events: TicketEvent[] }>(`/admin/support-ticket/${id}/events`);
};

// Internal notes — agent-only; the customer never sees these.
export const getTicketNotes = (id: Id) => {
    return api.get<{ notes: TicketNote[] }>(`/admin/support-ticket/${id}/notes`);
};

// POST a new internal note (body 1..2000 chars → 201 {note} | 400).
export const addTicketNote = (id: Id, body: string) => {
    return api.post<{ note: TicketNote }>(`/admin/support-ticket/${id}/note`, { body });
};

// Canned replies for the ⚡ quick-insert menu next to the reply box.
export const listCannedReplies = () => {
    return api.get<{ replies: CannedReply[] }>(`/admin/support-canned`);
};

// Search tickets across description / publicNumber / phone / uid, with optional city/status scoping.
export const searchSupportTickets = (q: string, opts?: { cityId?: string; status?: string; limit?: number; from?: string; to?: string }) => {
    const params = new URLSearchParams();
    params.append('q', q);
    if (opts?.cityId) params.append('cityId', opts.cityId);
    if (opts?.status) params.append('status', opts.status);
    if (opts?.limit) params.append('limit', opts.limit.toString());
    if (opts?.from) params.append('from', opts.from);
    if (opts?.to) params.append('to', opts.to);
    return api.get<{ tickets: SupportTicket[]; total: number }>(`/admin/support-tickets/search?${params.toString()}`);
};